export interface HudSail {
  level: number;
  angle: number;
}

export type HudRoleKind = 'deck' | 'helm' | 'sail';

export interface HudCannon {
  side: 'port' | 'starboard';
  loaded: boolean;
  cooldown: number;
}

export interface HudCrew {
  gunners: number;
  sailors: number;
  repairs: number;
  /** True while the repair hand is actively restoring hull. */
  repairing: boolean;
}

export interface HudWeather {
  /** 'calm' | 'building' | 'storm' | 'easing'. */
  phase: string;
  /** 0..1 overall storm intensity. */
  storm: number;
  /** 0..1 rain intensity. */
  rain: number;
  /** True on a lightning strike tick. */
  lightning: boolean;
}

export interface HudModel {
  health: number;
  playerCount: number;
  speed: number;
  /** Ship heading in radians. */
  heading: number;
  connection: string;
  /** Absolute wind direction in radians (the direction the wind blows toward). */
  windDirection: number;
  /** Wind strength in [0, 1]. */
  windStrength: number;
  sails: HudSail[];
  role: string;
  roleKind: HudRoleKind;
  status: string;
  /** True while the hull is destroyed and the defeat overlay is shown. */
  defeated: boolean;
  /** Per-side cannon status for the player ship. */
  cannons: HudCannon[];
  /** NPC crew roster summary. */
  crew: HudCrew;
  /** Server-authoritative storm weather summary. */
  weather: HudWeather;
  /** True while the shared anchor is down (server state). */
  anchored: boolean;
  /** Hull-to-drop-point distance, metres (0 when stowed). */
  anchorDistance: number;
}

const LEVEL_NAMES = ['furled', 'quarter', 'half', 'three-quarter', 'full'];

/** Wind strength buckets, ordered low → high. */
const WIND_LABELS: ReadonlyArray<{ max: number; label: string }> = [
  { max: 0.2, label: 'Calm' },
  { max: 0.45, label: 'Light' },
  { max: 0.75, label: 'Moderate' },
  { max: Infinity, label: 'Strong' },
];

/** Cosine-alignment thresholds separating efficient / passable / luffing sails. */
const ALIGN_GOOD = 0.55;
const ALIGN_OK = 0.15;
/** Alignment slack (radians) before the rotation hint is shown. */
const HINT_TOLERANCE = 0.12;

/**
 * Sailing HUD: ship health, crew count, speed/heading, connection status and an
 * intuitive wind/sail dashboard.
 *
 * The wind compass is drawn relative to the bow: its arrow points the way the
 * wind blows, so the crew can read the wind at a glance. Each sail has a small
 * dial whose boom rotates with the sail angle — match a boom to the wind arrow
 * and that sail is trimmed. The gauge is color-coded by how well the sail
 * catches the wind (green good, yellow ok, red luffing/into the wind) and shows
 * a hint arrow with the shortest way to rotate it.
 */
export class Hud {
  private readonly healthValue = this.getElement('#health-value');
  private readonly playersValue = this.getElement('#players-value');
  private readonly speedValue = this.getElement('#speed-value');
  private readonly headingValue = this.getElement('#heading-value');
  private readonly connectionValue = this.getElement('#connection-value');
  private readonly roleValue = this.getElement('#role-value');
  private readonly windIndicator = this.getElement('#wind-indicator');
  private readonly windArrow = this.getElement('#wind-arrow');
  private readonly windStrengthFill = this.getElement('#wind-strength-fill');
  private readonly windStrengthLabel = this.getElement('#wind-strength-label');
  private readonly windStrengthValue = this.getElement('#wind-strength-value');
  private readonly statusLine = this.getElement('#status-line');
  private readonly defeatOverlay = this.getElement('#defeat-overlay');
  private readonly restartButton = this.getElement('#restart-button');
  private readonly sailGauges: HTMLElement[] = [
    this.getElement('#sail-gauge-0'),
    this.getElement('#sail-gauge-1'),
  ];
  private readonly sailBooms: HTMLElement[] = [
    this.getElement('#sail0-arrow'),
    this.getElement('#sail1-arrow'),
  ];
  private readonly sailHints: HTMLElement[] = [
    this.getElement('#sail0-hint'),
    this.getElement('#sail1-hint'),
  ];
  private readonly sailValues: HTMLElement[] = [
    this.getElement('#sail0-value'),
    this.getElement('#sail1-value'),
  ];

  private readonly cannonRow: HTMLElement;
  private readonly cannonPort: { value: HTMLElement; fill: HTMLElement };
  private readonly cannonStarboard: { value: HTMLElement; fill: HTMLElement };
  private readonly crewValue: HTMLElement;
  private readonly stormValue: HTMLElement;
  private readonly anchorValue: HTMLElement;

  /** Invoked when the player presses Restart on the defeat overlay. */
  onRestart: (() => void) | null = null;

  constructor() {
    // Cannon status is built dynamically so `index.html` needs no additions.
    this.cannonRow = document.createElement('div');
    this.cannonRow.className = 'hud-row';
    this.getElement('#hud').appendChild(this.cannonRow);
    this.cannonPort = this.makeCannonMetric('Port Q');
    this.cannonStarboard = this.makeCannonMetric('Star R');

    // NPC crew roster readout, also built dynamically.
    const crewRow = document.createElement('div');
    crewRow.className = 'hud-row';
    this.getElement('#hud').appendChild(crewRow);
    this.crewValue = this.makeCrewMetric(crewRow);

    // Storm weather readout, also built dynamically.
    const weatherRow = document.createElement('div');
    weatherRow.className = 'hud-row';
    this.getElement('#hud').appendChild(weatherRow);
    this.stormValue = this.makeStormMetric(weatherRow);

    // Anchor status readout, also built dynamically.
    const anchorRow = document.createElement('div');
    anchorRow.className = 'hud-row';
    this.getElement('#hud').appendChild(anchorRow);
    this.anchorValue = this.makeAnchorMetric(anchorRow);

    const controlsHelp = this.getElement('#controls-help');
    controlsHelp.textContent =
      'WASD: Move | E: Interact | Q: Fire port | R: Fire starboard | F: Anchor';

    this.restartButton.addEventListener('click', () => this.onRestart?.());
  }

  update(model: HudModel): void {
    this.healthValue.textContent = String(Math.round(model.health));
    this.playersValue.textContent = String(model.playerCount);
    this.speedValue.textContent = model.speed.toFixed(1);
    this.headingValue.textContent = `${toDegrees(model.heading)}°`;
    this.connectionValue.textContent = model.connection;
    this.connectionValue.dataset.status = model.connection;
    this.roleValue.textContent = model.role;
    this.roleValue.dataset.role = model.roleKind;

    this.updateWind(model);
    this.updateSails(model);
    this.updateCannons(model.cannons);
    this.updateCrew(model.crew);
    this.updateWeather(model.weather);
    this.updateAnchor(model.anchored, model.anchorDistance);

    this.statusLine.textContent =
      model.roleKind === 'helm' ? 'W/S trim sails · Shift+A/D rotate' : model.status;
    this.defeatOverlay.hidden = !model.defeated;
  }

  private updateWind(model: HudModel): void {
    // Wind bearing relative to the bow. CSS rotate() is clockwise while world
    // yaw is counter-clockwise, so negate it: 0° points the arrow at the bow.
    const relative = wrapRadians(model.windDirection - model.heading);
    this.windArrow.style.transform = `rotate(${(-toDegrees(relative)).toFixed(1)}deg)`;
    this.windIndicator.setAttribute(
      'aria-label',
      `Wind blowing ${relativeLabel(relative)} of the bow`,
    );

    const strength = clamp01(model.windStrength);
    const percent = Math.round(strength * 100);
    this.windStrengthFill.style.width = `${percent}%`;
    this.windStrengthValue.textContent = `${percent}%`;
    this.windStrengthLabel.textContent = windLabel(strength);
  }

  private updateSails(model: HudModel): void {
    const windRelative = wrapRadians(model.windDirection - model.heading);

    model.sails.forEach((sail, index) => {
      const gauge = this.sailGauges[index];
      const boom = this.sailBooms[index];
      const hint = this.sailHints[index];
      const value = this.sailValues[index];
      if (!gauge || !boom || !hint || !value) return;

      const level = Math.max(0, Math.min(4, Math.round(sail.level)));
      const angleDeg = Math.round((sail.angle * 180) / Math.PI);

      // Counter-clockwise sail yaw maps to a clockwise CSS rotation, matching
      // the wind arrow's convention so the two can be visually aligned.
      boom.style.transform = `rotate(${-angleDeg}deg)`;

      // Thrust follows dot(sailFacing, wind), so quality is the cosine of the
      // angle between the sail's facing and the wind it catches.
      const alignment = Math.cos(wrapRadians(model.heading + sail.angle - model.windDirection));
      const quality = Math.max(0, alignment);
      const efficiency = quality * (level / 4);

      let state = 'idle';
      if (level > 0) {
        state = quality >= ALIGN_GOOD ? 'good' : quality >= ALIGN_OK ? 'ok' : 'bad';
      }
      gauge.className = `sail-gauge ${state}`;

      // Shortest rotation the sail still needs to line up with the wind arrow.
      const delta = wrapRadians(windRelative - sail.angle);
      if (level <= 0) {
        hint.textContent = '·';
      } else if (delta > HINT_TOLERANCE) {
        hint.textContent = '↺';
      } else if (delta < -HINT_TOLERANCE) {
        hint.textContent = '↻';
      } else {
        hint.textContent = '●';
      }

      value.textContent = `${LEVEL_NAMES[level]} · ${angleDeg > 0 ? '+' : ''}${angleDeg}°`;
      gauge.setAttribute(
        'aria-label',
        `${LEVEL_NAMES[level]} sail, ${angleDeg} degrees, ${Math.round(efficiency * 100)}% efficiency`,
      );
    });
  }

  private updateCannons(cannons: HudCannon[]): void {
    for (const cannon of cannons) {
      const metric = cannon.side === 'port' ? this.cannonPort : this.cannonStarboard;
      if (cannon.loaded) {
        metric.value.textContent = 'Loaded';
        metric.fill.style.width = '100%';
        metric.fill.style.background = '#7ee2b8';
      } else {
        const seconds = Math.max(0, cannon.cooldown);
        metric.value.textContent = `Reload ${seconds.toFixed(1)}s`;
        metric.fill.style.width = '0%';
        metric.fill.style.background = '#f08a8a';
      }
    }
  }

  private updateAnchor(anchored: boolean, distance: number): void {
    this.anchorValue.textContent = anchored
      ? `Dropped · ${distance.toFixed(0)}m (F to raise)`
      : 'Stowed (F to drop)';
    this.anchorValue.dataset.anchored = anchored ? 'true' : 'false';
  }

  private updateCrew(crew: HudCrew): void {
    this.crewValue.textContent =
      `${crew.gunners} gun · ${crew.sailors} sail · ${crew.repairs} repair` +
      (crew.repairing ? ' · repairing' : '');
    this.crewValue.dataset.repairing = crew.repairing ? 'true' : 'false';
  }

  private makeCannonMetric(label: string): { value: HTMLElement; fill: HTMLElement } {
    const metric = document.createElement('div');
    metric.className = 'hud-metric';

    const span = document.createElement('span');
    span.textContent = label;

    const value = document.createElement('strong');
    value.textContent = 'Loaded';

    const bar = document.createElement('div');
    bar.style.marginTop = '3px';
    bar.style.height = '4px';
    bar.style.borderRadius = '2px';
    bar.style.background = 'rgba(0, 0, 0, 0.4)';
    bar.style.overflow = 'hidden';

    const fill = document.createElement('div');
    fill.style.width = '100%';
    fill.style.height = '100%';
    fill.style.background = '#7ee2b8';
    bar.appendChild(fill);

    metric.append(span, value, bar);
    this.cannonRow.appendChild(metric);
    return { value, fill };
  }

  private updateWeather(weather: HudWeather): void {
    const phase = weather.phase || 'calm';
    const storm = Math.round(clamp01(weather.storm) * 100);
    const parts = [phase, `storm ${storm}%`];
    if (weather.lightning) parts.push('⚡');
    this.stormValue.textContent = parts.join(' · ');
    this.stormValue.dataset.phase = phase;
    this.stormValue.dataset.lightning = weather.lightning ? 'true' : 'false';
  }

  private makeCrewMetric(row: HTMLElement): HTMLElement {
    const metric = document.createElement('div');
    metric.className = 'hud-metric';

    const span = document.createElement('span');
    span.textContent = 'NPC Crew';

    const value = document.createElement('strong');
    value.textContent = '0 gun · 0 sail · 0 repair';

    metric.append(span, value);
    row.appendChild(metric);
    return value;
  }

  private makeAnchorMetric(row: HTMLElement): HTMLElement {
    const metric = document.createElement('div');
    metric.className = 'hud-metric';

    const span = document.createElement('span');
    span.textContent = 'Anchor';

    const value = document.createElement('strong');
    value.textContent = 'Stowed (F to drop)';

    metric.append(span, value);
    row.appendChild(metric);
    return value;
  }

  private makeStormMetric(row: HTMLElement): HTMLElement {
    const metric = document.createElement('div');
    metric.className = 'hud-metric';

    const span = document.createElement('span');
    span.textContent = 'Weather';

    const value = document.createElement('strong');
    value.textContent = 'calm · storm 0%';

    metric.append(span, value);
    row.appendChild(metric);
    return value;
  }

  private getElement(selector: string): HTMLElement {
    const element = document.querySelector<HTMLElement>(selector);
    if (!element) throw new Error(`Missing HUD element: ${selector}`);
    return element;
  }
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/** Wrap an angle into (-π, π]. */
function wrapRadians(angle: number): number {
  return Math.atan2(Math.sin(angle), Math.cos(angle));
}

function windLabel(strength: number): string {
  return WIND_LABELS.find((bucket) => strength < bucket.max)?.label ?? 'Strong';
}

function toDegrees(radians: number): number {
  const degrees = Math.round((radians * 180) / Math.PI) % 360;
  return degrees < 0 ? degrees + 360 : degrees;
}

/**
 * Coarse label for a wind bearing relative to the bow. Positive relative angles
 * fall to port and negative to starboard, matching the on-screen arrow.
 */
function relativeLabel(relative: number): string {
  const degrees = Math.abs((relative * 180) / Math.PI);
  if (degrees < 8) return 'dead ahead';
  if (degrees > 172) return 'dead astern';
  return `${Math.round(degrees)}° to ${relative > 0 ? 'port' : 'starboard'}`;
}
